import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Button, Slide, Menu, MenuItem, Box, Alert, Tooltip,
  Chip, CircularProgress, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { nanoid } from 'nanoid';
import { addEdge, applyNodeChanges, applyEdgeChanges } from 'reactflow';
import FlowCanvas from './FlowCanvas';
import DataSourceBlockConfig from './DataSourceBlockConfig';
import LogicBlockConfig from './LogicBlockConfig';
import OutputBlockConfig, { buildOutputSourceGroups, outputBindingIsUnavailable } from './OutputBlockConfig';
import PredefinedBlockConfig from './PredefinedBlockConfig';
import PublishFunctionDialog from './PublishFunctionDialog';
import { db, storage } from '../../services/firebase';
import { ref, uploadString, deleteObject } from "firebase/storage";
import { doc, getDoc, collection, getDocs, onSnapshot, deleteField } from 'firebase/firestore';
import { formatText } from '../common/TextFormatterProvider';
import HelpButton from '../common/HelpButton';
import { loadFailureMessage } from '../common/LoadFailure';
import { commitDraftMutation, commitDisplayMutation, isEditLeaseLost } from '../../utils/functionMutation';
import { isEmailFeatureEnabled } from '../../utils/outputRegistry';
import {
  LEASE_HEARTBEAT_MS,
  acquireEditLease,
  heartbeatEditLease,
  isPublishedVersion,
  releaseEditLease,
  takeOverEditLease,
} from '../../services/functionVersions';
import { watch as watchContacts } from '../../services/emailContacts';
import { consumeSessionMount, leaseStartedLabel, registerSessionMount } from '../../services/editLease';
import { assistantRunExpiryMillis } from '../../services/assistantRunLease';
import { useAuth } from '../../contexts/AuthContext';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// A Predefined block reads tables — its mappings name a table's columns — so
// only a Data Source feeds it. A Logic block reads envelopes, so a Predefined
// block may feed it. The backend refuses the one edge this matrix forbids
// between processors (executable_from_live).
const ALLOWED_CONNECTIONS = new Set([
  'data_source:logic',
  'data_source:predefined',
  'predefined:logic',
  'predefined:output',
  'logic:logic',
  'logic:output',
]);

export const isAllowedFunctionConnection = (sourceType, targetType) => (
  ALLOWED_CONNECTIONS.has(`${sourceType}:${targetType}`)
);

// A function graph only runs forward. A connection whose target already leads
// back to its source — directly (a self-loop) or through other blocks — would
// make a cycle the runner rejects at validation, so it is refused before it
// is ever drawn or saved.
export const wouldCreateCycle = (edges, source, target) => {
  if (!source || !target) return false;
  if (source === target) return true;
  const seen = new Set([target]);
  const stack = [target];
  while (stack.length > 0) {
    const node = stack.pop();
    for (const edge of edges) {
      if (edge.source !== node) continue;
      if (edge.target === source) return true;
      if (!seen.has(edge.target)) {
        seen.add(edge.target);
        stack.push(edge.target);
      }
    }
  }
  return false;
};

// Every executable node must lie on a path to an Output: a dangling branch
// still executes on every run and its failure aborts results it cannot
// influence (M204). An Output itself must have a connected input.
export const unreachableNodeIds = (nodes, edges) => {
  const reachesOutput = new Set(
    nodes.filter((node) => node.data?.type === 'output').map((node) => node.id),
  );
  let grew = true;
  while (grew) {
    grew = false;
    for (const edge of edges) {
      if (reachesOutput.has(edge.target) && !reachesOutput.has(edge.source)) {
        reachesOutput.add(edge.source);
        grew = true;
      }
    }
  }
  return nodes
    .filter((node) => node.data?.type === 'output'
      ? !edges.some(edge => edge.target === node.id && nodes.some(source => source.id === edge.source))
      : !reachesOutput.has(node.id))
    .map((node) => node.id);
};

// A Logic or Predefined block's preview objects are addressed by full URI, as
// the Logic Assistant's implement writes them.
const previewObjectUri = (path) => `gs://${process.env.REACT_APP_PLATFORM_FIREBASE_STORAGE_BUCKET}/${path}`;

// Every storage object a block's saved preview consists of: a Data Source's
// rows, or a processor's rows and files. Bucket-relative paths and gs:// URIs
// alike, both of which ref() accepts.
export const previewObjectPaths = (block) => [
  block?.preview_data_gcs_path,
  block?.output_data_gcs_path,
  ...(block?.artifacts_manifest || []).filter(entry => entry.kind === 'file').map(entry => entry.gcs_path),
].filter(Boolean);

// Disconnecting a producer removes only its selections on affected Outputs.
// Other destinations and the producer's manifest remain intact. The caller
// commits these writes in the same transaction as its graph edit.
export const pruneDisconnectedOutputs = (nodes, edges, blockConfigs) => {
  const writes = [];
  for (const node of nodes) {
    if (node.data?.type !== 'output') continue;
    const blockId = node.data.blockId;
    const config = blockConfigs[blockId]?.config;
    if (!config) continue;
    const incomingBlockIds = new Set(edges.filter(edge => edge.target === node.id)
      .map(edge => nodes.find(source => source.id === edge.source)?.data?.blockId));
    const nextConfig = { ...config };
    let changed = false;
    for (const field of ['bindings', 'attachments']) {
      if (!Array.isArray(config[field])) continue;
      const retained = config[field].filter(entry => incomingBlockIds.has(entry.source_block_id));
      if (retained.length !== config[field].length) {
        nextConfig[field] = retained;
        changed = true;
      }
    }
    if (changed) writes.push({ blockId, op: 'update', data: { config: nextConfig } });
  }
  return writes;
};

// Verdict for one head-document snapshot seen by the takeover watch. Cache
// emissions are ignored: acquireEditLease writes the lease in a transaction,
// which the server applies without updating the local cache, so the watch's
// first snapshot is a cached copy that still shows the pre-acquire lease
// (null on a just-created function). Only a server-confirmed snapshot may
// evict the editor — a real takeover always arrives as one.
export const evaluateLeaseSnapshot = (snap, sessionId) => {
  if (snap.metadata?.fromCache) return 'ignore';
  if (!snap.exists()) return 'deleted';
  const lease = snap.data()?.edit_lease;
  if (!lease || lease.session_id !== sessionId) return 'lost';
  return 'held';
};

// The watch synchronizes on its own stream before it may evict: the acquire
// transaction commits on the server only, so even a server-flagged snapshot
// can predate it when the Dashboard's collection listener already had the
// head document in sync. Until this stream reads back the lease this session
// just wrote, a leaseless or foreign snapshot is a pre-acquire echo, not a
// takeover. After that confirmation, anything without our lease evicts. (A
// takeover so fast that our lease never appears at all is caught by the
// heartbeat and by the lease check on every write.)
export const makeLeaseWatchHandler = ({ sessionId, isHeld, onVerdict }) => {
  let confirmed = false;
  return (snap) => {
    if (!isHeld()) return;
    const verdict = evaluateLeaseSnapshot(snap, sessionId);
    if (verdict === 'held') { confirmed = true; return; }
    if (verdict === 'ignore' || !confirmed) return;
    onVerdict(verdict);
  };
};

export const getOutputBindingErrors = ({ nodes, edges, blockConfigs }) => {
  const errorsByNodeId = {};

  nodes
    .filter((node) => node.data?.type === 'output')
    .forEach((outputNode) => {
      const outputBlock = blockConfigs[outputNode.data.blockId] || {};
      const bindings = outputBlock.config?.bindings || [];
      const groups = buildOutputSourceGroups({
        nodes,
        edges,
        outputNode,
        blockConfigs,
      });
      const groupsByBlockId = new Map(groups.map((group) => [group.sourceBlockId, group]));
      const bindingErrors = [];

      bindings.forEach((binding) => {
        if (!outputBindingIsUnavailable(binding, groups)) return;
        const group = groupsByBlockId.get(binding.source_block_id);

        const sourceBlock = blockConfigs[binding.source_block_id] || {};
        const sourceName = group?.name || formatText(sourceBlock.name || binding.source_block_id);
        // A Predefined block has entries only once it has been previewed and
        // saved; until then the binding is not stale, it is waiting.
        const awaitingPreview = group?.sourceType === 'predefined' && group.entries.length === 0;
        bindingErrors.push({
          sourceBlockId: binding.source_block_id,
          entryKey: binding.entry_key,
          message: awaitingPreview
            ? `Output references \`${binding.entry_key}\` on ${sourceName}, which has not been previewed. Open that block and save it.`
            : `Output references \`${binding.entry_key}\`, which ${sourceName} no longer produces.`,
        });
      });

      if (bindingErrors.length > 0) errorsByNodeId[outputNode.id] = bindingErrors;
    });

  return errorsByNodeId;
};

function FunctionBuilderDialog({
  open, onClose, onSave, tables = [], companyName, functionId, sessionId
}) {
  const { currentUser } = useAuth() || {};
  const actorUid = currentUser?.uid || 'unknown';
  const actorName = currentUser?.displayName || currentUser?.email || 'Another user';

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [apis, setApis] = useState([]);
  // The predefined catalogue is a global read; when it fails the Add menu says so
  // rather than silently offering no Predefined entries.
  const [apisStatus, setApisStatus] = useState('loading');
  const apisError = apisStatus === 'error';
  const [blockConfigs, setBlockConfigs] = useState({});
  const [assistantLeaseTick, refreshAssistantLeases] = useState(0);
  const assistantRunning = Object.values(blockConfigs).some(
    block => assistantRunExpiryMillis(block) > Date.now());
  useEffect(() => {
    const now = Date.now();
    const expiries = Object.values(blockConfigs).map(assistantRunExpiryMillis)
      .filter(expiry => Number.isFinite(expiry) && expiry > now);
    if (expiries.length === 0) return undefined;
    // Expiry itself causes no Firestore event; refresh the button locally.
    const timer = setTimeout(() => refreshAssistantLeases(value => value + 1),
      Math.min(...expiries) - now + 10);
    return () => clearTimeout(timer);
  }, [blockConfigs, assistantLeaseTick]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [configuringNodeId, setConfiguringNodeId] = useState(null);
  const [configApi, setConfigApi] = useState(null);
  const [apiOpenErrorNodeId, setApiOpenErrorNodeId] = useState(null);
  const [apiOpening, setApiOpening] = useState(false);
  const configOpenToken = useRef(0);
  useEffect(() => {
    setConfiguringNodeId(null);
    setApiOpening(false);
    setApiOpenErrorNodeId(null);
    return () => { configOpenToken.current += 1; };
  }, [open, companyName, functionId]);
  const [error, setError] = useState('');
  const [loadingFunction, setLoadingFunction] = useState(false);
  // The initial read of the function head failed, or the head no longer exists.
  // Terminal for this session: the graph on screen is the empty initial state,
  // not the saved one, so nothing may be edited, published, or written back.
  const [loadFailure, setLoadFailure] = useState('');
  // True only once the saved graph has actually been read into `nodes`/`edges`.
  // persistCanvasLayout is the single write of those arrays, and it is gated on
  // this: with the lease held but the read failed, the close path used to write
  // the empty initial arrays over the saved graph.
  const graphLoadedRef = useRef(false);
  const [functionName, setFunctionName] = useState('');
  // Company email contacts, loaded only when the Email feature is enabled, for
  // the Output editor's recipient picker.
  const [contacts, setContacts] = useState([]);
  const [companyDisplayName, setCompanyDisplayName] = useState('');
  // Publish state. `publishedVersion` is the version runs execute today;
  // `hasUnpublishedChanges` is the advisory dirty flag every draft write sets.
  const [publishedVersion, setPublishedVersion] = useState(null);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  // Set by commitDraft: the close guard fires only for edits made in THIS
  // session, not for a draft that was already ahead of the published version
  // when the builder opened.
  const dirtiedThisSessionRef = useRef(false);
  const committedLogicGenerationsRef = useRef(null);
  // Unacknowledged Logic Assistant text and files, keyed by blockId. Lifted here so an
  // accidental Save/Cancel in the node config does not destroy a typed
  // message. Dies with the builder (Dashboard unmounts it on close); a
  // deleted node drops its entry.
  const [logicDrafts, setLogicDrafts] = useState({});

  // --- Advisory edit lease --------------------------------------------------
  // One editing session at a time holds the lease. It is advisory only: any user
  // may take it over with confirmation, and a crashed session's lease simply
  // expires. Nothing about execution depends on it.
  // `sessionId` identifies one editing session and is owned by the Dashboard,
  // which mints it when the builder opens and drops it when it closes. This
  // editor therefore claims the lease ONCE and holds it: a remount rejoins the
  // same session (acquireEditLease treats this session's own lease as
  // available) instead of arriving as a stranger its own live lease refuses.
  const leaseHeldRef = useRef(false);
  const releaseRef = useRef(null);
  // The one claim of this editing session, with the document it is for, so the
  // release path can wait for a claim still in flight and the result of a claim
  // for a document no longer on screen can be discarded.
  const claimRef = useRef(null);
  // One editing session of one document: what the claim and the mount registry
  // are both keyed by.
  const editSessionKey = `${sessionId}/${companyName}/${functionId}`;
  const [leaseState, setLeaseState] = useState('acquiring');
  const [blockingLease, setBlockingLease] = useState(null);
  const [takeoverBusy, setTakeoverBusy] = useState(false);

  const handleEditLeaseLost = useCallback(() => {
    leaseHeldRef.current = false;
    setLeaseState('lost');
  }, []);

  const releaseIfHeld = useCallback(async (mountToken) => {
    // A claim still in flight has not set leaseHeldRef yet, so wait for it
    // rather than read the flag too early and leave the lease on the document
    // until it expires. A failed claim reports itself and leaves nothing to
    // release.
    const claim = claimRef.current;
    if (claim) await claim.request.catch(() => {});
    // Another mount is editing under this session now, so the lease is being
    // used: this one is on its way out and has nothing to hand back.
    if (!consumeSessionMount(editSessionKey, mountToken)) return;
    if (!leaseHeldRef.current) return;
    leaseHeldRef.current = false;
    await releaseEditLease(companyName, functionId, sessionId);
  }, [companyName, functionId, sessionId, editSessionKey]);

  useEffect(() => { releaseRef.current = releaseIfHeld; }, [releaseIfHeld]);

  // Release on unmount so closing the browser tab's editor frees the lease
  // immediately instead of waiting for the 15 minute expiry.
  const unmountedRef = useRef(false);
  const mountTokenRef = useRef(null);
  useEffect(() => {
    // This mount is the one editing under `sessionId` until another replaces
    // it. Development remounts this editor, so the unmounted flag has to start
    // false again for the mount that is actually on screen.
    const mountToken = registerSessionMount(editSessionKey);
    mountTokenRef.current = mountToken;
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      const release = releaseRef.current;
      if (release) {
        release(mountToken).catch((err) => console.error('Failed to release edit lease:', err));
      }
    };
  }, [editSessionKey]);

  useEffect(() => {
    if (!open || !companyName || !functionId) return undefined;
    // One claim per editing session per document. This effect also re-runs when
    // the actor's display name resolves and when development remounts the
    // editor; neither is a new editing session, so neither claims again. A new
    // session always claims.
    if (claimRef.current?.claimKey === editSessionKey) return undefined;
    setLeaseState('acquiring');
    const claim = {
      claimKey: editSessionKey,
      request: acquireEditLease(companyName, functionId, {
        uid: actorUid,
        holderName: actorName,
        sessionId,
      }),
    };
    claimRef.current = claim;
    claim.request
      .then((result) => {
        // A grant puts the lease on the head document whatever happens next, so
        // record it before anything else: the close and unmount paths read this
        // flag to hand the lease back instead of leaving it to expire — which
        // would block the next open behind a "take over from yourself".
        if (result.granted) leaseHeldRef.current = true;
        if (claimRef.current !== claim) return;
        if (result.granted) {
          setLeaseState('held');
        } else {
          setBlockingLease(result.lease);
          setLeaseState('blocked');
        }
      })
      .catch((err) => {
        if (claimRef.current !== claim) return;
        console.error('Failed to acquire the edit lease:', err);
        setBlockingLease(null);
        setLeaseState('unavailable');
      });
    return undefined;
  }, [open, companyName, functionId, actorUid, actorName, sessionId, editSessionKey]);

  useEffect(() => {
    if (leaseState !== 'held' || !companyName || !functionId) return undefined;
    const timer = setInterval(async () => {
      try {
        const outcome = await heartbeatEditLease(companyName, functionId, sessionId);
        if (outcome !== 'held') {
          leaseHeldRef.current = false;
          setLeaseState(outcome);
        }
      } catch (err) {
        console.error('Edit lease heartbeat failed:', err);
      }
    }, LEASE_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [leaseState, companyName, functionId, sessionId]);

  // Takeover is streamed, not polled: the lease lives on the head document,
  // so the moment another session replaces it this editor exits instead of
  // waiting up to a full heartbeat interval (H52). The heartbeat above still
  // renews the lease's expiry.
  useEffect(() => {
    if (leaseState !== 'held' || !companyName || !functionId) return undefined;
    const headRef = doc(db, `users/${companyName}/data_functions`, functionId);
    const watchLease = makeLeaseWatchHandler({
      sessionId,
      isHeld: () => leaseHeldRef.current,
      onVerdict: (verdict) => {
        leaseHeldRef.current = false;
        setLeaseState(verdict);
      },
    });
    return onSnapshot(headRef, { includeMetadataChanges: true }, (snapshot) => {
      watchLease(snapshot);
      if (!snapshot.exists() || snapshot.metadata?.fromCache || snapshot.metadata?.hasPendingWrites) return;
      const head = snapshot.data();
      if (leaseHeldRef.current && head.edit_lease?.session_id === sessionId) {
        setHasUnpublishedChanges(head.has_unpublished_changes === true);
      }
    }, (err) => {
      console.error('Failed to watch the edit lease:', err);
    });
  }, [leaseState, companyName, functionId, sessionId]);

  // A Logic Assistant stream still running when the lease is lost is aborted:
  // the backend lease check would reject its commit anyway, so stop the
  // generation rather than let it run on for a closed editor (H52).
  const assistantAbortRef = useRef(null);
  useEffect(() => {
    if (leaseState === 'lost' || leaseState === 'deleted') {
      assistantAbortRef.current?.abort();
      assistantAbortRef.current = null;
    }
  }, [leaseState]);

  const handleTakeOver = async () => {
    setTakeoverBusy(true);
    try {
      await takeOverEditLease(companyName, functionId, {
        uid: actorUid,
        holderName: actorName,
        sessionId,
      });
      if (unmountedRef.current) {
        // The editor closed while the takeover was in flight: the lease just
        // written would be orphaned (the unmount release has already run), so
        // hand it straight back.
        await releaseEditLease(companyName, functionId, sessionId);
        return;
      }
      leaseHeldRef.current = true;
      setBlockingLease(null);
      setLeaseState('held');
      // The graph on screen was read while the previous holder was still
      // editing, so it may be behind what they saved before this takeover.
      // Reload the authoritative head and blocks before anything here can be
      // edited or persisted over theirs.
      await loadFunctionAndBlocks();
    } catch (err) {
      console.error('Failed to take over the edit lease:', err);
      setLeaseState('unavailable');
    } finally {
      setTakeoverBusy(false);
    }
  };

  const loadFunctionAndBlocks = useCallback(async () => {
    if (!companyName || !functionId) return;
    setLoadingFunction(true);
    setLoadFailure('');
    graphLoadedRef.current = false;
    try {
      const docRef = doc(db, `users/${companyName}/data_functions`, functionId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        setLoadFailure('This function no longer exists.');
        return;
      }
      const fData = docSnap.data();
      setNodes(fData.nodes || []);
      setEdges(fData.edges || []);
      graphLoadedRef.current = true;
      setFunctionName(fData.name || '');
      setHasUnpublishedChanges(fData.has_unpublished_changes === true);
      setPublishedVersion(isPublishedVersion(fData.published_version) ? fData.published_version : null);

      const blocksRef = collection(db, `users/${companyName}/data_functions`, functionId, 'blocks');
      const blocksSnap = await getDocs(blocksRef);
      const loadedConfigs = {};
      blocksSnap.forEach(blockDoc => {
        loadedConfigs[blockDoc.id] = blockDoc.data();
      });
      if (committedLogicGenerationsRef.current === null) {
        committedLogicGenerationsRef.current = Object.fromEntries(
          Object.entries(loadedConfigs).map(([id, block]) => [id, block.manifest_generation || null]),
        );
      }
      setBlockConfigs(loadedConfigs);
    } catch (err) {
      console.error("Error loading function:", err);
      // A failed blocks read after a successful head read is recoverable — the
      // live subscription fills the configs — but the graph itself must have
      // loaded for the editor to be safe to show.
      if (!graphLoadedRef.current) {
        setLoadFailure('This function could not be loaded.');
      } else {
        setError("Failed to load function data.");
      }
    } finally {
      setLoadingFunction(false);
    }
  }, [companyName, functionId]);

  // Load the company display name only when the Email feature is enabled.
  const loadEmailContext = useCallback(async () => {
    if (!companyName || !isEmailFeatureEnabled()) return;
    try {
      const companySnap = await getDoc(doc(db, 'users', companyName));
      setCompanyDisplayName(companySnap.exists() ? (companySnap.data()?.display_name || '') : '');
    } catch (err) {
      console.error('Failed to load company display name:', err);
    }
  }, [companyName]);

  // Company contacts stream while the builder is open, so a contact added in
  // Settings (the Output editor links there in a new tab) reaches the
  // recipient picker without reopening the builder.
  useEffect(() => {
    if (!open || !companyName || !isEmailFeatureEnabled()) return undefined;
    return watchContacts(companyName, setContacts, (err) => {
      console.error('Failed to subscribe to email contacts:', err);
    });
  }, [open, companyName]);

  const loadApis = useCallback(async () => {
    setApisStatus('loading');
    try {
      const apisRef = collection(db, 'apis');
      const apisSnap = await getDocs(apisRef);
      const apiList = apisSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setApis(apiList);
      setApisStatus('ready');
    } catch (err) {
      console.error('Failed to load the predefined block catalogue:', err);
      setApisStatus('error');
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadFunctionAndBlocks();
      loadApis();
      loadEmailContext();
    }
  }, [open, loadFunctionAndBlocks, loadApis, loadEmailContext]);

  useEffect(() => {
    if (!open || !companyName || !functionId) return undefined;

    const blocksRef = collection(db, `users/${companyName}/data_functions`, functionId, 'blocks');
    committedLogicGenerationsRef.current = null;
    return onSnapshot(blocksRef, { includeMetadataChanges: true }, (snapshot) => {
      const liveConfigs = {};
      snapshot.forEach((blockDoc) => {
        liveConfigs[blockDoc.id] = blockDoc.data();
      });
      if (!snapshot.metadata?.fromCache && !snapshot.metadata?.hasPendingWrites) {
        const generations = Object.fromEntries(
          Object.entries(liveConfigs).map(([id, block]) => [id, block.manifest_generation || null]),
        );
        const previous = committedLogicGenerationsRef.current;
        if (previous && leaseHeldRef.current && Object.entries(generations).some(
          ([id, generation]) => generation && generation !== previous[id],
        )) {
          // Only a saved implementation advances this field. Exploration,
          // sandbox bookkeeping and conversation updates never do.
          dirtiedThisSessionRef.current = true;
        }
        committedLogicGenerationsRef.current = generations;
      }
      setBlockConfigs(liveConfigs);
    }, (err) => {
      console.error('Failed to subscribe to function blocks:', err);
      setError('Failed to receive live block updates.');
    });
  }, [open, companyName, functionId]);

  const outputBindingErrors = useMemo(() => getOutputBindingErrors({
    nodes,
    edges,
    blockConfigs,
  }), [nodes, edges, blockConfigs]);

  // Every graph, block, and chart edit is a DRAFT write: it changes nothing that
  // executes, so it needs no safety side effect. It only records that the draft
  // has moved ahead of the published version.
  const commitDraft = useCallback(async ({ functionUpdate = {}, blockWrites = [] }) => {
    try {
      await commitDraftMutation(
        db, companyName, functionId, { functionUpdate, blockWrites },
        sessionId,
      );
    } catch (err) {
      // A save refused because another session holds the lease ends this
      // editing session on the spot: nothing was written (H52).
      if (isEditLeaseLost(err)) {
        leaseHeldRef.current = false;
        setLeaseState('lost');
      }
      throw err;
    }
    dirtiedThisSessionRef.current = true;
    setHasUnpublishedChanges(true);
  }, [companyName, functionId, sessionId]);

  // Canvas geometry is display-only: it is stripped from published content, so
  // it writes directly and never sets the dirty flag. It writes only a graph
  // that was actually read from the document (see graphLoadedRef).
  const persistCanvasLayout = useCallback(async () => {
    if (!companyName || !functionId || !graphLoadedRef.current) return;
    // nodes/edges ARE the executable graph, so even this display write is
    // lease-checked: a displaced session must not overwrite the new editor's
    // canvas (H52).
    try {
      await commitDisplayMutation(
        db, companyName, functionId, { nodes, edges }, sessionId);
    } catch (err) {
      if (isEditLeaseLost(err)) {
        leaseHeldRef.current = false;
        setLeaseState('lost');
        return;
      }
      throw err;
    }
  }, [companyName, functionId, nodes, edges, sessionId]);

  const handleOpenMenu = (event) => setMenuAnchor(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  // The predefined blocks a data source node feeds.
  const predefinedTargetsOf = useCallback((sourceNodeId) => (
    edges
      .filter((edge) => edge.source === sourceNodeId)
      .map((edge) => nodes.find((n) => n.id === edge.target))
      .filter((node) => node?.data.type === 'predefined' && node.data.blockId)
      .map((node) => node.data.blockId)
  ), [nodes, edges]);

  // A predefined block records, per API source, the data source block feeding
  // it and column mappings into that block's table. When a feeding source is
  // disconnected or deleted its entry is dropped; when the source's table
  // changes (`newTableId`) its mappings are cleared. Either way the block asks
  // to be configured again rather than run on columns that no longer exist.
  // Returns the block writes for commitDraft — none when nothing referenced
  // the sources.
  const prunePredefinedSources = useCallback((predefinedBlockId, sourceBlockIds, newTableId = null) => {
    const block = blockConfigs[predefinedBlockId];
    const sources = block?.config?.data_sources || {};
    let changed = false;
    const nextSources = {};
    Object.entries(sources).forEach(([key, value]) => {
      const untouched = !sourceBlockIds.has(value?.source_node_id)
        || (newTableId && value.table_id === newTableId);
      if (untouched) {
        nextSources[key] = value;
        return;
      }
      changed = true;
      if (newTableId) nextSources[key] = { ...value, table_id: newTableId, mappings: {} };
    });
    if (!changed) return [];
    return [{
      blockId: predefinedBlockId,
      op: 'update',
      data: { configured: false, config: { ...block.config, data_sources: nextSources } },
    }];
  }, [blockConfigs]);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  // The SDK retries a batch through any network failure and only rejects for
  // good (document gone, permission refused), so a rejection here means the
  // canvas is showing an edit that will never land: it is undone. Each undo is
  // functional — it reverts just this edit, not whatever else changed meanwhile.
  const onEdgesChange = useCallback((changes) => {
    const nextEdges = applyEdgeChanges(changes, edges);
    setEdges(nextEdges);
    // Removing a connection changes the executable graph, so it is committed as
    // a draft edit rather than left to the next display write.
    const removedIds = new Set(changes.filter((change) => change.type === 'remove').map((change) => change.id));
    if (removedIds.size > 0) {
      const removedEdges = edges.filter((edge) => removedIds.has(edge.id));
      // Predefined blocks losing a data source, grouped so a block losing two
      // at once gets one write.
      const disconnected = new Map();
      removedEdges.forEach((edge) => {
        const sourceNode = nodes.find((n) => n.id === edge.source);
        const targetNode = nodes.find((n) => n.id === edge.target);
        if (sourceNode?.data.type !== 'data_source' || targetNode?.data.type !== 'predefined') return;
        if (!sourceNode.data.blockId || !targetNode.data.blockId) return;
        if (!disconnected.has(targetNode.data.blockId)) disconnected.set(targetNode.data.blockId, new Set());
        disconnected.get(targetNode.data.blockId).add(sourceNode.data.blockId);
      });
      const blockWrites = [...disconnected].flatMap(([predefinedBlockId, sourceBlockIds]) =>
        prunePredefinedSources(predefinedBlockId, sourceBlockIds));
      blockWrites.push(...pruneDisconnectedOutputs(nodes, nextEdges, blockConfigs));
      commitDraft({ functionUpdate: { edges: nextEdges }, blockWrites }).then(() => {
        setBlockConfigs(previous => {
          const next = { ...previous };
          blockWrites.forEach(write => { next[write.blockId] = { ...next[write.blockId], ...write.data }; });
          return next;
        });
      }).catch((err) => {
        console.error('Failed to remove connection:', err);
        setError('Failed to remove the connection. Please try again.');
        setEdges((current) => [
          ...current,
          ...removedEdges.filter((edge) => !current.some((existing) => existing.id === edge.id)),
        ]);
      });
    }
  }, [nodes, edges, blockConfigs, commitDraft, prunePredefinedSources]);

  const handleDeleteNode = async (nodeId) => {
    if (!companyName || !functionId) return;

    try {
      const nodeToDelete = nodes.find(n => n.id === nodeId);
      if (!nodeToDelete) return;

      const { blockId } = nodeToDelete.data;

      const updatedNodes = nodes.filter(n => n.id !== nodeId);
      const updatedEdges = edges.filter(e => e.source !== nodeId && e.target !== nodeId);

      setNodes(updatedNodes);
      setEdges(updatedEdges);

      const blockWrites = blockId ? [{ blockId, op: 'delete' }] : [];
      if (blockId && nodeToDelete.data.type === 'data_source') {
        predefinedTargetsOf(nodeId).forEach((predefinedBlockId) => {
          blockWrites.push(...prunePredefinedSources(predefinedBlockId, new Set([blockId])));
        });
      }
      blockWrites.push(...pruneDisconnectedOutputs(updatedNodes, updatedEdges, blockConfigs));
      await commitDraft({
        functionUpdate: { nodes: updatedNodes, edges: updatedEdges },
        blockWrites,
      });

      setBlockConfigs(previous => {
        const next = { ...previous };
        blockWrites.forEach(write => {
          if (write.op === 'delete') delete next[write.blockId];
          else next[write.blockId] = { ...next[write.blockId], ...write.data };
        });
        return next;
      });
      if (blockId) {
        setLogicDrafts(prev => {
          if (!(blockId in prev)) return prev;
          const next = { ...prev };
          delete next[blockId];
          return next;
        });
      }

      if (nodeId === configuringNodeId) {
        setConfiguringNodeId(null);
      }
    } catch (err) {
      console.error('Error deleting node:', err);
      setError('Failed to delete block. Please try again.');
      const nodeToRestore = nodes.find(n => n.id === nodeId);
      if (nodeToRestore) {
        const edgesToRestore = edges.filter(e => e.source === nodeId || e.target === nodeId);
        setNodes((current) => (current.some(n => n.id === nodeId) ? current : [...current, nodeToRestore]));
        setEdges((current) => [
          ...current,
          ...edgesToRestore.filter((edge) => !current.some((existing) => existing.id === edge.id)),
        ]);
      }
    }
  };

  const onConnect = useCallback(async (params) => {
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);

    if (!sourceNode || !targetNode) return;

    const sourceType = sourceNode.data.type;
    const targetType = targetNode.data.type;
    if (!isAllowedFunctionConnection(sourceType, targetType)) {
      setError('That connection is not supported. Data sources feed logic or predefined blocks; predefined blocks feed logic or output blocks; logic blocks feed logic or output blocks.');
      return;
    }
    if (wouldCreateCycle(edges, params.source, params.target)) {
      setError('That connection would feed a block\'s output back into a block that feeds it. Connections only run forward.');
      return;
    }

    setError('');
    const newEdges = addEdge(params, edges);
    setEdges(newEdges);

    if (!companyName || !functionId) return;

    const sourceBlockId = sourceNode.data.blockId;
    const targetBlockId = targetNode.data.blockId;
    const blockWrites = [];

    // A data_source -> logic edge records the logic block on the source block.
    if (sourceBlockId && targetBlockId && sourceType === 'data_source' && targetType === 'logic') {
      blockWrites.push({ blockId: sourceBlockId, op: 'update', data: { logic_block_id: targetBlockId } });
    }

    try {
      await commitDraft({
        functionUpdate: { nodes, edges: newEdges },
        blockWrites,
      });
    } catch (err) {
      console.error('Failed to add connection:', err);
      setError('Failed to add the connection. Please try again.');
      const addedIds = new Set(newEdges.filter((edge) => !edges.some((existing) => existing.id === edge.id)).map((edge) => edge.id));
      setEdges((current) => current.filter((edge) => !addedIds.has(edge.id)));
    }
  }, [edges, nodes, companyName, functionId, commitDraft]);

  // The canvas asks this while a connection is being dragged, so a handle that
  // cannot legally be connected refuses the drop instead of accepting it and
  // then reporting an error.
  const isValidConnection = useCallback((connection) => {
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    if (!sourceNode || !targetNode) return false;
    return isAllowedFunctionConnection(sourceNode.data.type, targetNode.data.type)
      && !wouldCreateCycle(edges, connection.source, connection.target);
  }, [nodes, edges]);

  const addNode = async (type, apiId = null) => {
    handleCloseMenu();
    let nodeTypeKey;
    if (type === 'data_source') nodeTypeKey = 'dataSourceNode';
    if (type === 'logic') nodeTypeKey = 'logicNode';
    if (type === 'output') nodeTypeKey = 'outputNode';
    if (type === 'predefined') nodeTypeKey = 'predefinedNode';

    const nodeId = `node_${type}_${nanoid(6)}`;
    const blockId = `${type}_block_${nanoid(6)}`;

    const newNode = {
      id: nodeId,
      type: nodeTypeKey,
      position: { x: Math.random() * 500, y: Math.random() * 300 },
      data: { type, blockId, config: {} }
    };

    let blockData = { id: blockId, name: '', configured: false, type };
    if (type === 'predefined' && apiId) {
        blockData.config = { api_id: apiId, data_sources: {}, constants: {} };
    }
    if (type === 'output') {
        blockData.config = { output_type: 'decode_ui', bindings: [] };
    }

    const updatedNodes = [...nodes, newNode];
    setNodes(updatedNodes);

    try {
        await commitDraft({
          functionUpdate: { nodes: updatedNodes },
          blockWrites: [{ blockId, op: 'set', data: blockData }],
        });

        setBlockConfigs(prev => ({ ...prev, [blockId]: blockData }));
    } catch (err) {
        console.error("Failed to add node:", err);
        setError("Could not add new block.");
        setNodes(nodes);
    }
  };

  const handleConfigureNode = async (nodeId) => {
    const token = ++configOpenToken.current;
    setApiOpenErrorNodeId(null);
    setApiOpening(false);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    if (node.data.type === 'logic' || node.data.type === 'output' || node.data.type === 'predefined') {
      const hasIncomingEdge = edges.some(edge => edge.target === nodeId);
      if (!hasIncomingEdge) {
        setError(`Please connect an incoming edge to this block before configuring it.`);
        return;
      }
    }
    if (node.data.type === 'logic') {
      // The Logic Assistant mounts an upstream Predefined block's saved
      // preview, and a Predefined block is configured only by a save, which
      // always previews first.
      const unpreviewed = edges.filter(edge => edge.target === nodeId)
        .map(edge => nodes.find(n => n.id === edge.source))
        .find(source => source?.data.type === 'predefined' && !blockConfigs[source.data.blockId]?.configured);
      if (unpreviewed) {
        const label = formatText(blockConfigs[unpreviewed.data.blockId]?.name || 'Predefined');
        setError(`Preview and save the predefined block "${label}" before configuring this logic block.`);
        return;
      }
    }
    setError('');
    if (node.data.type === 'predefined') {
      setApiOpening(true);
      try {
        const apiId = blockConfigs[node.data.blockId]?.config?.api_id;
        if (!apiId) throw new Error('This block has no API declaration.');
        const snapshot = await getDoc(doc(db, 'apis', apiId));
        if (!snapshot.exists()) throw new Error('This block’s API declaration no longer exists.');
        const definition = snapshot.data();
        if (!definition.inputs || typeof definition.inputs !== 'object' || Array.isArray(definition.inputs)
            || !Array.isArray(definition.manifest)) {
          throw new Error('This block’s API declaration is incomplete.');
        }
        if (token !== configOpenToken.current) return;
        setConfigApi({ ...definition, id: apiId });
      } catch (error) {
        if (token !== configOpenToken.current) return;
        setError(`Could not open this block: ${error.message}`);
        setApiOpenErrorNodeId(nodeId);
        return;
      } finally {
        if (token === configOpenToken.current) setApiOpening(false);
      }
    }
    setConfiguringNodeId(nodeId);
  };

  const handleConfigCancel = () => {
    configOpenToken.current += 1;
    setApiOpening(false);
    setConfiguringNodeId(null);
  };

  // The open block editor, asked `isDirty()` / `save()` by Publish and Close so
  // an edit typed into it cannot be published past or closed away silently.
  const blockEditorRef = useRef(null);
  // What to resume once a Publish- or Close-triggered save has closed the
  // editor: the resumed handler then reads the block state that save produced
  // (a handler continuing across its own `await` would read the stale render).
  const afterEditorSaveRef = useRef(null);
  const [confirmBlockCloseOpen, setConfirmBlockCloseOpen] = useState(false);
  useEffect(() => {
    if (!afterEditorSaveRef.current || configuringNodeId) return;
    const next = afterEditorSaveRef.current;
    afterEditorSaveRef.current = null;
    if (next === 'publish') handlePublish();
    else handleRequestClose();
  });

  const UNSAVED_BLOCK_MESSAGE = 'The open block could not be saved — fix what its editor shows, then try again.';
  const saveOpenEditor = async (thenDo) => {
    afterEditorSaveRef.current = thenDo;
    const saved = await blockEditorRef.current.save();
    if (!saved) {
      afterEditorSaveRef.current = null;
      setError(UNSAVED_BLOCK_MESSAGE);
    }
    return saved;
  };

  // Returns whether the block was committed; the editor's own error explains a false.
  const handleSaveBlockConfig = async (blockId, newConfig) => {
    if (!companyName || !functionId || !blockId) return false;

    // Preview data is stored in Storage, not Firestore, and Storage cannot join
    // a Firestore batch. So the preview goes to NEW objects nobody references
    // yet, and the batch below swaps the block's pointers to them together
    // with the configuration: refused, the block still names its old,
    // untouched preview (never the old configuration with the new rows — which
    // the Logic Assistant would otherwise read as one). Whichever objects end
    // up unreferenced are deleted best-effort; a survivor is harmless.
    const previousPreviewObjects = previewObjectPaths(blockConfigs[blockId]);
    const uploadedPreviewObjects = [];
    let committed = false;
    const discardObjects = (paths) => {
      paths.forEach((path) => {
        deleteObject(ref(storage, path)).catch((err) => {
          console.warn(`Could not delete superseded preview ${path}:`, err);
        });
      });
    };

    try {
        // Preview data is stored in GCS, not Firestore; strip it before the write.
        const cfg = { ...newConfig };
        const previewFolder = `users/${companyName}/functions/${functionId}/blocks/${blockId}/previews/${nanoid(10)}`;
        const uploadPreviewObject = async (leaf, content, contentType) => {
            const path = `${previewFolder}/${leaf}`;
            await uploadString(ref(storage, path), content, 'raw', { contentType });
            uploadedPreviewObjects.push(path);
            return path;
        };
        if (cfg.preview_data) {
            // A Data Source's preview: its rows, at a bucket-relative path.
            const previewData = cfg.preview_data;
            delete cfg.preview_data;
            cfg.preview_data_gcs_path = await uploadPreviewObject(
              'preview_data.json', JSON.stringify(previewData), 'application/json');
        }
        if (cfg.preview) {
            // A Predefined block's preview: its rows and files, carried on the
            // block exactly as a Logic block's implement carries its own, so
            // every downstream reader sees one shape.
            const preview = cfg.preview;
            delete cfg.preview;
            const rowsPath = await uploadPreviewObject('data.json', JSON.stringify(preview.rows), 'application/json');
            cfg.output_data_gcs_path = previewObjectUri(rowsPath);
            cfg.artifacts_manifest = [];
            for (const entry of preview.manifest) {
                if (entry.kind !== 'file') {
                    cfg.artifacts_manifest.push(entry);
                    continue;
                }
                const file = preview.files.find(candidate => candidate.filename === entry.key);
                const filePath = await uploadPreviewObject(entry.key, file.content, entry.mime);
                cfg.artifacts_manifest.push({ ...entry, gcs_path: previewObjectUri(filePath) });
            }
        }

        const nodeToUpdate = nodes.find(n => n.data.blockId === blockId);
        const configured = !blockConfigs[blockId]?.logic_revalidation_required;
        let updatedNodes = nodes;
        if (nodeToUpdate) {
            const updatedNodeData = {
                ...nodeToUpdate.data,
                config: { ...nodeToUpdate.data.config, name: cfg.name, configured }
            };
            updatedNodes = nodes.map(n => n.id === nodeToUpdate.id ? { ...n, data: updatedNodeData } : n);
        }

        const blockWrites = [{ blockId, op: 'update', data: { ...cfg, configured } }];
        const newTableId = cfg.config?.table_id;
        if (nodeToUpdate?.data.type === 'data_source' && newTableId) {
          // Changing the source table invalidates its consumers' mappings in
          // the same transaction. A failed save must leave both unchanged.
          predefinedTargetsOf(nodeToUpdate.id).forEach(predefinedBlockId => {
            blockWrites.push(...prunePredefinedSources(predefinedBlockId, new Set([blockId]), newTableId));
          });
          if (newTableId !== blockConfigs[blockId]?.config?.table_id) {
            const targets = new Set(edges.filter(edge => edge.source === nodeToUpdate.id).map(edge => edge.target));
            nodes.filter(node => targets.has(node.id) && node.data.type === 'logic').forEach(node => {
              blockWrites.push({
                blockId: node.data.blockId,
                op: 'update',
                data: {
                  configured: false,
                  logic_revalidation_required: true,
                  logic_revalidation_reason: 'The source table changed. Update this block with the Logic Assistant before publishing.',
                  // The source and this fence move together. A turn that mounted
                  // the previous table must not publish code after this save.
                  assistant_run_lease: null,
                  sandbox_id: deleteField(),
                  sandbox_created_at: deleteField(),
                  scratch_context_id: deleteField(),
                },
              });
            });
          }
        }
        await commitDraft({
          functionUpdate: { nodes: updatedNodes },
          blockWrites,
        });
        committed = true;
        if (uploadedPreviewObjects.length > 0) {
          discardObjects(previousPreviewObjects);
        }

        // Update local state so every consumer sees the latest config.
        setBlockConfigs(previous => {
          const next = { ...previous };
          blockWrites.forEach(write => {
            next[write.blockId] = { ...next[write.blockId], ...write.data };
          });
          return next;
        });
        if (nodeToUpdate) setNodes(updatedNodes);

        handleConfigCancel();
        return true;
    } catch (err) {
        console.error("Failed to save block config:", err);
        setError("Could not save configuration.");
        // Only objects the batch never pointed at are discarded.
        if (!committed) discardObjects(uploadedPreviewObjects);
        return false;
    }
  };

  // Browser-side sanity checks before spending a full validation run. The
  // backend re-validates everything and is the real gate.
  const getPublishBlocker = () => {
    const repair = Object.values(blockConfigs).find(block => block.logic_revalidation_required);
    if (repair) return `${formatText(repair.name || 'Logic block')}: ${repair.logic_revalidation_reason}`;
    const outputNodes = nodes.filter((node) => node.data.type === 'output');
    if (outputNodes.length < 1) {
      return 'A published function must contain at least one Output block.';
    }
    const disconnected = unreachableNodeIds(nodes, edges);
    if (disconnected.length > 0) {
      const names = disconnected.map(id => {
        const node = nodes.find(candidate => candidate.id === id);
        return formatText(blockConfigs[node.data?.blockId]?.name || id);
      });
      return `These blocks are disconnected: ${names.join(', ')}. Connect or remove them before publishing.`;
    }
    for (const outputNode of outputNodes) {
      const block = blockConfigs[outputNode.data.blockId] || {};
      const config = block.config || {};
      const label = formatText(block.name || 'Output');
      if (!block.configured) {
        return `Configure the "${label}" Output block before publishing.`;
      }
      if (config.output_type === 'decode_ui') {
        if (!Array.isArray(config.bindings) || config.bindings.length === 0) {
          return `The "${label}" Output block needs at least one selected entry.`;
        }
      } else if (config.output_type === 'email') {
        if (!Array.isArray(config.recipient_contact_ids) || config.recipient_contact_ids.length === 0) {
          return `The "${label}" Email output needs at least one recipient.`;
        }
        if (!Array.isArray(config.attachments) || config.attachments.length === 0) {
          return `The "${label}" Email output needs at least one attachment.`;
        }
        if (!config.subject || !config.body) {
          return `The "${label}" Email output needs a subject and body.`;
        }
      } else {
        return `The "${label}" Output block has an unsupported destination.`;
      }
    }
    if (Object.keys(outputBindingErrors).length > 0) {
      return 'Resolve the highlighted output binding errors before publishing.';
    }
    return null;
  };

  const handlePublish = async () => {
    if (assistantRunning) {
      setError('Wait for the Logic Assistant to finish before publishing.');
      return;
    }
    // An edit still sitting in the open block editor is part of what is being
    // published: save it first, and publish from the render that has it.
    if (blockEditorRef.current?.isDirty()) {
      await saveOpenEditor('publish');
      return;
    }
    const blocker = getPublishBlocker();
    if (blocker) {
      setError(blocker);
      return;
    }
    setError('');
    try {
      await persistCanvasLayout();
    } catch (err) {
      console.error('Failed to persist canvas layout before publishing:', err);
    }
    // A function that has never been published still needs a name. The Dashboard
    // naming dialog owns that step and starts the publish once it is named.
    if (!functionName.trim()) {
      await handleCloseEditor(() => onSave({ id: functionId }));
      return;
    }
    setPublishOpen(true);
  };

  const handlePublished = useCallback((result) => {
    setPublishedVersion(result.publishedVersion);
    setHasUnpublishedChanges(false);
  }, []);

  // Closing the publish dialog after a successful publish finishes the editing
  // session: the work is saved as a version, so leave the canvas and return to
  // the dashboard. Closing it without publishing (or after a failure) keeps the
  // builder open so the user can fix the function.
  const handleClosePublish = (published) => {
    setPublishOpen(false);
    if (published) handleCloseEditor();
  };

  const handleCloseEditor = async (afterClose = null) => {
    try {
      if (leaseHeldRef.current) await persistCanvasLayout();
    } catch (err) {
      console.error('Failed to persist canvas layout:', err);
    }
    try {
      await releaseIfHeld(mountTokenRef.current);
    } catch (err) {
      console.error('Failed to release edit lease:', err);
    }
    if (afterClose) afterClose();
    onClose();
  };

  // X / Esc land here: edits made this session that are still unpublished get
  // a confirmation instead of a silent close.
  const handleRequestClose = () => {
    if (blockEditorRef.current?.isDirty()) {
      setConfirmBlockCloseOpen(true);
      return;
    }
    if (dirtiedThisSessionRef.current && hasUnpublishedChanges) {
      setConfirmCloseOpen(true);
      return;
    }
    handleCloseEditor();
  };

  const configuringNode = nodes.find(n => n.id === configuringNodeId);

  if (!open) return null;

  if (leaseState === 'blocked' || leaseState === 'unavailable') {
    return (
      <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>This function is being edited</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {leaseState === 'blocked'
              ? `${blockingLease?.holder_name || 'Another user'} is editing this function (started ${leaseStartedLabel(blockingLease)}).`
              : 'The edit lock for this function could not be read.'}
            {' '}Taking over ends their editing session. Their work is already saved — the draft
            autosaves — but they will need to reopen the builder.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} disabled={takeoverBusy}>Cancel</Button>
          <Button onClick={handleTakeOver} variant="contained" disabled={takeoverBusy}>
            {takeoverBusy ? 'Taking over…' : 'Take over'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (leaseState === 'lost' || leaseState === 'deleted') {
    return (
      <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>{leaseState === 'deleted' ? 'This function was deleted' : 'Editing session ended'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {leaseState === 'deleted'
              ? 'Another user deleted this function while you were editing it, so this editor has been closed.'
              : 'Another session took over editing this function, so this editor has been closed. Your '
                + 'saved changes are safe; reopen the builder to continue.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (leaseState === 'acquiring' || loadingFunction) {
    return (
      <Dialog fullScreen open={open} TransitionComponent={Transition}>
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  // The lease is held but the saved graph never arrived, so there is no canvas
  // to show. Close releases the lease; persistCanvasLayout is a no-op here.
  if (loadFailure) {
    return (
      <Dialog open onClose={() => handleCloseEditor()} maxWidth="sm" fullWidth>
        <DialogTitle>Function unavailable</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {loadFailure} Your saved work has not been changed. Close the builder and try again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseEditor()} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog fullScreen open={open} onClose={handleRequestClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleRequestClose}><CloseIcon /></IconButton>
          <Typography sx={{ ml: 2 }} variant="h6" component="div">Build Function</Typography>
          <HelpButton
            docPath="category/1-working-with-data-functions"
            tooltip="Functions Documentation"
            sx={{ color: 'inherit', ml: 1 }}
          />
          <Box sx={{ flex: 1 }} />
          {hasUnpublishedChanges ? (
            <Tooltip title="Runs still execute the published version until you publish these changes.">
              <Chip size="small" color="warning" label="Unpublished changes" sx={{ mr: 1 }} />
            </Tooltip>
          ) : (
            <Chip
              size="small"
              variant="outlined"
              sx={{ mr: 1, color: 'inherit', borderColor: 'currentColor' }}
              label={publishedVersion ? `Published v${publishedVersion}` : 'Not published'}
            />
          )}
          <Button color="inherit" startIcon={<AddIcon />} onClick={handleOpenMenu}>Add Block</Button>
          <Button color="inherit" onClick={handlePublish} disabled={assistantRunning}>Publish</Button>
        </Toolbar>
      </AppBar>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => addNode('data_source')}>Data Source</MenuItem>

        <MenuItem onClick={() => addNode('logic')}>Logic</MenuItem>

        <MenuItem onClick={() => addNode('output')}>Output</MenuItem>

        {apis.map(api => (
          <MenuItem
            key={api.id}
            onClick={() => addNode('predefined', api.id)}
            disabled={apisStatus !== 'ready' || !api.inputs || !api.manifest}
          >
            Predefined: {formatText(api.name)}
          </MenuItem>
        ))}
        {apisError && (
          <MenuItem disabled sx={{ whiteSpace: 'normal', maxWidth: 320 }}>
            {loadFailureMessage('predefined blocks')}
          </MenuItem>
        )}
        {apisError && (
          <MenuItem onClick={() => { handleCloseMenu(); loadApis(); }}>Retry</MenuItem>
        )}
      </Menu>

      {apiOpening && <Alert severity="info">Loading block configuration…</Alert>}
      {error && (
        <Box sx={{ position: 'absolute', top: '70px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
          <Alert severity="error" onClose={() => setError('')} variant="filled" action={apiOpenErrorNodeId ? (
            <Button color="inherit" onClick={() => handleConfigureNode(apiOpenErrorNodeId)}>Retry</Button>
          ) : null}>{error}</Alert>
        </Box>
      )}

      {apisError && (
        <Alert severity="warning" action={<Button onClick={loadApis}>Retry catalogue</Button>}>
          The Predefined catalogue could not be loaded, so no Predefined block can be added.
        </Alert>
      )}
      <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex' }}>
        <Box sx={{ flex: 1, height: '100%' }}>
          <FlowCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onConfigureNode={handleConfigureNode}
            onDeleteNode={handleDeleteNode}
            outputBindingErrors={outputBindingErrors}
            logicRepairReasons={Object.fromEntries(nodes
              .filter(node => blockConfigs[node.data.blockId]?.logic_revalidation_required)
              .map(node => [node.id, blockConfigs[node.data.blockId].logic_revalidation_reason]))}
          />
        </Box>
      </Box>

      {configuringNode && configuringNode.data.type === 'data_source' && (
        <DataSourceBlockConfig
          ref={blockEditorRef}
          key={configuringNode.data.blockId}
          blockId={configuringNode.data.blockId}
          companyName={companyName}
          functionId={functionId}
          tables={tables}
          onSave={handleSaveBlockConfig}
          onCancel={handleConfigCancel}
        />
      )}

      {configuringNode && configuringNode.data.type === 'logic' && (
        <LogicBlockConfig
          ref={blockEditorRef}
          key={configuringNode.data.blockId}
          companyName={companyName}
          functionId={functionId}
          blockId={configuringNode.data.blockId}
          sessionId={sessionId}
          onEditLeaseLost={handleEditLeaseLost}
          onImplementationCommitted={() => {
            dirtiedThisSessionRef.current = true;
            setHasUnpublishedChanges(true);
          }}
          registerAssistantAbort={(controller) => { assistantAbortRef.current = controller; }}
          draftMessage={logicDrafts[configuringNode.data.blockId]?.text || ''}
          draftFiles={logicDrafts[configuringNode.data.blockId]?.files || []}
          draftSubmissionId={logicDrafts[configuringNode.data.blockId]?.submissionId || null}
          onDraftMessageChange={(text) => {
            const { blockId } = configuringNode.data;
            setLogicDrafts(prev => ({
              ...prev, [blockId]: { ...prev[blockId], text, submissionId: null },
            }));
          }}
          onDraftFilesChange={(files) => {
            const { blockId } = configuringNode.data;
            setLogicDrafts(prev => ({
              ...prev, [blockId]: { ...prev[blockId], files, submissionId: null },
            }));
          }}
          onDraftSubmitted={(submissionId) => {
            const { blockId } = configuringNode.data;
            setLogicDrafts(prev => ({
              ...prev, [blockId]: { ...prev[blockId], submissionId },
            }));
          }}
          onDraftSaved={(submissionId) => {
            const { blockId } = configuringNode.data;
            setLogicDrafts(prev => {
              if (prev[blockId]?.submissionId !== submissionId) return prev;
              const next = { ...prev };
              delete next[blockId];
              return next;
            });
          }}
          onSave={(config) => handleSaveBlockConfig(configuringNode.data.blockId, config)}
          onCancel={handleConfigCancel}
        />
      )}

      {configuringNode && configuringNode.data.type === 'output' && (
        <OutputBlockConfig
          ref={blockEditorRef}
          key={configuringNode.data.blockId}
          blockConfig={blockConfigs[configuringNode.data.blockId]}
          nodes={nodes}
          edges={edges}
          outputNode={configuringNode}
          blockConfigs={blockConfigs}
          contacts={contacts}
          companyDisplayName={companyDisplayName}
          onSave={(config) => handleSaveBlockConfig(configuringNode.data.blockId, config)}
          onCancel={handleConfigCancel}
        />
      )}

      {configuringNode && configuringNode.data.type === 'predefined' && (
        <PredefinedBlockConfig
          apiDetails={configApi}
          ref={blockEditorRef}
          key={configuringNode.data.blockId}
          companyName={companyName}
          functionId={functionId}
          blockId={configuringNode.data.blockId}
          onSave={(config) => handleSaveBlockConfig(configuringNode.data.blockId, config)}
          onCancel={handleConfigCancel}
        />
      )}

      {/* The open block editor holds an unsaved edit: closing must not lose it silently. */}
      <Dialog open={confirmBlockCloseOpen} onClose={() => setConfirmBlockCloseOpen(false)} maxWidth="xs">
        <DialogTitle>Unsaved block changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The open block has changes that have not been saved. Save them, or discard them and close?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBlockCloseOpen(false)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmBlockCloseOpen(false);
              afterEditorSaveRef.current = 'close';
              handleConfigCancel();
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmBlockCloseOpen(false);
              saveOpenEditor('close');
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close guard: Esc / click-away dismisses back to editing. */}
      <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)} maxWidth="xs">
        <DialogTitle>Unpublished changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unpublished changes on this function. Until they are published they will not
            be included in future runs. Do you wish to publish?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmCloseOpen(false); handleCloseEditor(); }}>No</Button>
          <Button variant="contained" onClick={() => { setConfirmCloseOpen(false); handlePublish(); }}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {publishOpen && (
        <PublishFunctionDialog
          open={publishOpen}
          onClose={handleClosePublish}
          companyName={companyName}
          functionId={functionId}
          actorUid={actorUid}
          sessionId={sessionId}
          onPublished={handlePublished}
          onEditLeaseLost={handleEditLeaseLost}
        />
      )}
    </Dialog>
  );
}

export default FunctionBuilderDialog;
